if (!customElements.get('bundle-widget')) {
  customElements.define(
    'bundle-widget',
    class BundleWidget extends HTMLElement {
      connectedCallback() {
        this.config = this.readConfig();
        if (!this.config || !this.config.bundleVariants) return;

        this._syncingOptions = false;
        this._bundleTitleParts = this.buildBundleTitlePartsSet();

        this.initElements();
        this.initFixedDisplayPricing();
        this.bindEvents();
        this.syncOptionAvailability();
        this.observeOptionMutations();
        this.syncMainProductFromConfig();
        this.checkCombination();
      }

      disconnectedCallback() {
        this.unbindEvents();
        if (this._optionObserver) {
          this._optionObserver.disconnect();
          this._optionObserver = null;
        }
      }

      initElements() {
        this.mainVariantId = String(this.config.mainProduct ? this.config.mainProduct.selectedVariantId : '');
        this.mainVariantInput = this.querySelector('[data-main-variant-input]');
        this.submitButton = this.querySelector('[data-bundle-add-to-cart]');
        this.buttonText = this.querySelector('[data-bundle-button-text]');
        this.bundleVariantIdInput = this.querySelector('[data-bundle-variant-id]');

        this.defaultButtonText = this.dataset.buttonText || 'Add to cart';
        this.unavailableText =
          this.dataset.unavailableText || this.dataset.soldOutText || 'Unavailable';
      }

      initFixedDisplayPricing() {
        const cfg = this.config && this.config.displayPricing;
        if (!cfg) return;

        const bundleTotalCents = Number(cfg.bundleTotalCents) || 0;
        const compareTotalCents = Number(cfg.compareTotalCents) || 0;
        const saveCents = Number(cfg.saveCents) || 0;
        const savePercent = Number(cfg.savePercent) || 0;

        this.fixedDisplayPricing = {
          bundleTotalCents,
          compareTotalCents,
          saveCents,
          savePercent
        };
      }

      applyFixedDisplayPricing() {
        if (!this.fixedDisplayPricing) return;

        const { bundleTotalCents, compareTotalCents, saveCents, savePercent } =
          this.fixedDisplayPricing;
        const discountAmount =
          saveCents > 0
            ? saveCents
            : Math.max(0, compareTotalCents - bundleTotalCents);

        this.updateSaveBadge(savePercent);
        this.updatePricingUI(compareTotalCents, bundleTotalCents, discountAmount);
      }

      bindEvents() {
        this.onMainVariantChange = this.onMainVariantChange.bind(this);
        this.onBundleVariantChange = this.onBundleVariantChange.bind(this);

        document.addEventListener('theme:variant:change', this.onMainVariantChange);

        this.querySelectorAll('[data-bundle-variant-select]').forEach(select => {
          select.addEventListener('change', this.onBundleVariantChange);
        });
      }

      unbindEvents() {
        document.removeEventListener('theme:variant:change', this.onMainVariantChange);
        this.querySelectorAll('[data-bundle-variant-select]').forEach(select => {
          select.removeEventListener('change', this.onBundleVariantChange);
        });
      }

      readConfig() {
        const configEl = this.querySelector('[data-bundle-config]');
        if (!configEl) return null;

        try {
          return JSON.parse(configEl.textContent);
        } catch (error) {
          console.warn('Bundle Widget: invalid configuration JSON.', error);
          return null;
        }
      }

      buildBundleTitlePartsSet() {
        const parts = new Set();
        (this.config.bundleVariants || []).forEach(combination => {
          const name = (combination.variant_name || '').toLowerCase().trim();
          if (!name) return;
          name.split(' / ').forEach(part => {
            const trimmed = part.trim();
            if (trimmed) parts.add(trimmed);
          });
          parts.add(name);
        });
        return parts;
      }

      isPlaceholderTitle(title) {
        return !title || title.toLowerCase().trim() === 'default title';
      }

      isMatchableTitle(title) {
        if (!title || this.isPlaceholderTitle(title)) return false;
        const normalized = title.toLowerCase().trim();
        if (this._bundleTitleParts.has(normalized)) return true;
        // Multi-part title: matchable if every part exists on some bundle name
        const parts = normalized.split(' / ').map(p => p.trim()).filter(Boolean);
        if (parts.length > 1 && parts.every(p => this._bundleTitleParts.has(p))) {
          return true;
        }
        return false;
      }

      normalizeTitleParts(title) {
        return (title || '')
          .toLowerCase()
          .trim()
          .split(' / ')
          .map(part => part.trim())
          .filter(part => part && !this.isPlaceholderTitle(part));
      }

      sortedKey(parts) {
        return [...parts].map(p => p.toLowerCase().trim()).filter(Boolean).sort().join(' / ');
      }

      onMainVariantChange(event) {
        if (!event.detail || !event.detail.variant) return;
        if (this._syncingToMainForm) return;
        if (!this.isEventForThisProduct(event)) return;

        const variant = event.detail.variant;
        this.mainVariantId = String(variant.id);

        if (this.mainVariantInput) {
          this.mainVariantInput.value = this.mainVariantId;
        }

        this.syncMainProductUI(variant);
        this.syncOptionAvailability();
        this.checkCombination();
      }

      isEventForThisProduct(event) {
        if (!this.config || !this.config.mainProduct) return false;

        const widgetSection = this.closest('.shopify-section');
        const eventTarget = event.target;
        if (!widgetSection || !eventTarget || typeof eventTarget.closest !== 'function') {
          return true;
        }

        const eventSection = eventTarget.closest('.shopify-section');
        return !eventSection || widgetSection === eventSection;
      }

      syncMainProductFromConfig() {
        if (!this.config || !this.config.mainProduct) return;

        const mainVariant = this.config.mainProduct.variants.find(
          v => String(v.id) === String(this.mainVariantId)
        );
        if (mainVariant) {
          this.syncMainProductUI(mainVariant);
        }
      }

      syncMainProductUI(variant) {
        if (!variant || !this.config || !this.config.mainProduct) return;

        const mainProductId = String(this.config.mainProduct.id);
        const productContainer = this.querySelector(
          `[data-bundle-product][data-product-id="${mainProductId}"]`
        );
        if (!productContainer) return;

        const variantId = String(variant.id);
        const configVariant =
          this.config.mainProduct.variants.find(v => String(v.id) === variantId) || variant;

        const select = productContainer.querySelector('[data-bundle-variant-select]');
        if (select) {
          this._syncingMainProduct = true;
          select.value = variantId;
          this._syncingMainProduct = false;
        }

        const labelEl = productContainer.querySelector('[data-bundle-variant-label]');
        if (labelEl) {
          if (this.isPlaceholderTitle(configVariant.title)) {
            labelEl.hidden = true;
          } else {
            labelEl.textContent = configVariant.title;
            labelEl.hidden = false;
          }
        }

        const imageUrl = configVariant.featuredImage || this.config.mainProduct.featuredImage;
        if (imageUrl) {
          this.updateVariantImage(mainProductId, imageUrl);
        }
      }

      onBundleVariantChange(event) {
        if (this._syncingMainProduct) return;

        const select = event.target;
        if (select && select.dataset.isMainProduct === 'true') {
          this.mainVariantId = String(select.value || '');
          if (this.mainVariantInput) {
            this.mainVariantInput.value = this.mainVariantId;
          }
          this.syncToMainProductForm(this.mainVariantId);
          this.syncMainProductUI(
            this.config.mainProduct.variants.find(v => String(v.id) === this.mainVariantId)
          );
          this.checkCombination();
          return;
        }

        this.checkCombination();
      }

      runWithoutScroll(callback) {
        const scrollX = window.scrollX || window.pageXOffset || 0;
        const scrollY = window.scrollY || window.pageYOffset || 0;
        const savedThemeScrollTo = window.theme && window.theme.scrollTo;
        const savedWindowScrollTo = window.scrollTo.bind(window);
        const savedScrollIntoView = Element.prototype.scrollIntoView;

        if (window.theme) {
          window.theme.scrollTo = () => {};
        }
        window.scrollTo = () => {};
        Element.prototype.scrollIntoView = function () {};

        try {
          callback();
        } finally {
          if (window.theme && savedThemeScrollTo) {
            window.theme.scrollTo = savedThemeScrollTo;
          }
          window.scrollTo = savedWindowScrollTo;
          Element.prototype.scrollIntoView = savedScrollIntoView;

          const restoreScroll = () => {
            window.scrollTo(scrollX, scrollY);
          };

          restoreScroll();
          requestAnimationFrame(restoreScroll);
          setTimeout(restoreScroll, 450);
        }
      }

      syncToMainProductForm(variantId) {
        if (!variantId || this._syncingToMainForm) return;

        const section = this.closest('.shopify-section');
        if (!section) return;

        const idInput = section.querySelector('form[action*="/cart/add"] [name="id"]');
        if (!idInput || String(idInput.value) === String(variantId)) return;

        const variant = (this.config.mainProduct.variants || []).find(
          v => String(v.id) === String(variantId)
        );
        if (!variant) return;

        this._syncingToMainForm = true;

        this.runWithoutScroll(() => {
          const titleParts = this.isPlaceholderTitle(variant.title)
            ? []
            : variant.title.split(' / ').map(part => part.trim()).filter(Boolean);

          if (titleParts.length) {
            const optionInputs = section.querySelectorAll('[data-single-option-selector]');
            titleParts.forEach(part => {
              optionInputs.forEach(input => {
                if (input.value !== part && input.value.toLowerCase() !== part.toLowerCase()) {
                  return;
                }

                if (input.type === 'radio' || input.type === 'checkbox') {
                  input.checked = true;
                } else {
                  input.value = part;
                }
                input.dispatchEvent(new Event('change', { bubbles: true }));
              });
            });
          } else {
            idInput.value = variantId;
            idInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });

        this._syncingToMainForm = false;
      }

      getProductConfig(productId) {
        if (this.config.mainProduct && String(this.config.mainProduct.id) === String(productId)) {
          return this.config.mainProduct;
        }
        return this.findLinkedProduct(productId);
      }

      getTotalIndividualPrice() {
        let total = 0;

        this.querySelectorAll('[data-bundle-variant-select]').forEach(select => {
          const productId = select.dataset.productId;
          const variantId = String(select.value || '');
          if (!productId || !variantId) return;

          const product = this.getProductConfig(productId);
          if (!product) return;

          const variant = (product.variants || []).find(v => String(v.id) === variantId);
          if (variant && variant.price) {
            total += variant.price;
          }
        });

        if (total > 0) return total;

        const { mainPrice } = this.getMainProductDetails();
        const { linkedPrice } = this.getLinkedProductsDetails();
        return mainPrice + linkedPrice;
      }

      checkCombination() {
        const { mainTitles, mainAvailable } = this.getMainProductDetails();
        const { linkedTitles } = this.getLinkedProductsDetails();

        const mainTitleParts = mainTitles
          .map(title => title.toLowerCase().trim())
          .filter(title => !this.isPlaceholderTitle(title));

        const linkedMatchable = linkedTitles
          .map(title => title.toLowerCase().trim())
          .filter(title => this.isMatchableTitle(title));

        const linkedFallback = linkedTitles
          .map(title => title.toLowerCase().trim())
          .filter(title => !this.isPlaceholderTitle(title));

        let titlesForMatch = [...mainTitleParts];
        if (linkedMatchable.length > 0) {
          titlesForMatch = [...mainTitleParts, ...linkedMatchable];
        } else if (linkedFallback.length > 0) {
          titlesForMatch = [...mainTitleParts, ...linkedFallback];
        }

        const match = this.findMatchingBundleVariant(titlesForMatch);

        if (match && match.available && mainAvailable) {
          this.setAvailable(match);
        } else {
          this.setUnavailable();
        }

        this.applyFixedDisplayPricing();
      }

      getMainProductDetails() {
        const details = { mainTitles: [], mainPrice: 0, mainAvailable: true };
        if (!this.config || !this.config.mainProduct) return details;

        const mainVariant = this.config.mainProduct.variants.find(
          v => String(v.id) === String(this.mainVariantId)
        );
        if (mainVariant) {
          details.mainPrice = mainVariant.price || 0;
          details.mainAvailable = mainVariant.available !== false;
          if (!this.isPlaceholderTitle(mainVariant.title)) {
            this.normalizeTitleParts(mainVariant.title).forEach(part => {
              details.mainTitles.push(part);
            });
          }
        } else if (this.config.mainProduct.basePrice) {
          details.mainPrice = this.config.mainProduct.basePrice;
        }

        return details;
      }

      getLinkedProductsDetails() {
        const details = { linkedTitles: [], linkedPrice: 0 };
        const mainProductId = this.config.mainProduct
          ? String(this.config.mainProduct.id)
          : null;

        this.querySelectorAll('[data-bundle-variant-select]').forEach(select => {
          const productId = String(select.dataset.productId || '');

          if (mainProductId && productId === mainProductId) {
            return;
          }

          const variantId = String(select.value || '');
          if (!productId || !variantId) return;

          const product = this.findLinkedProduct(productId);
          if (!product) return;

          const variant = (product.variants || []).find(v => String(v.id) === variantId);
          if (!variant) return;

          details.linkedTitles.push(variant.title.toLowerCase().trim());
          if (variant.price) {
            details.linkedPrice += variant.price;
          }

          const imageUrl = variant.featuredImage || product.featuredImage;
          if (imageUrl) {
            this.updateVariantImage(productId, imageUrl);
          }
        });

        // Fallback when selects are empty/unready: use config selected variants
        if (!details.linkedTitles.length && this.config.linkedProducts) {
          this.config.linkedProducts.forEach(product => {
            if (mainProductId && String(product.id) === mainProductId) {
              return;
            }

            const variant =
              (product.variants || []).find(
                v => String(v.id) === String(product.selectedVariantId)
              ) || (product.variants || [])[0];
            if (!variant) return;

            details.linkedTitles.push(variant.title.toLowerCase().trim());
            if (variant.price) {
              details.linkedPrice += variant.price;
            }
          });
        }

        return details;
      }

      findLinkedProduct(productId) {
        if (!this.config || !this.config.linkedProducts) return null;
        return this.config.linkedProducts.find(p => String(p.id) === String(productId)) || null;
      }

      updateVariantImage(productId, imageUrl) {
        const productContainer = this.querySelector(
          `[data-bundle-product][data-product-id="${productId}"]`
        );
        if (productContainer) {
          const images = productContainer.querySelector('[data-bundle-product-image]');
          if (images && images.tagName === 'IMG') {
            images.src = imageUrl;
            images.srcset = '';
          }
        }
      }

      findMatchingBundleVariant(selectedTitles) {
        if (!selectedTitles || !selectedTitles.length || !this.config.bundleVariants) return null;

        const selectedParts = [];
        selectedTitles.forEach(title => {
          this.normalizeTitleParts(title).forEach(part => {
            if (!selectedParts.includes(part)) selectedParts.push(part);
          });
        });

        if (!selectedParts.length) return null;

        const exactKey = this.sortedKey(selectedParts);

        // Prefer exact match (sorted option parts equal bundle variant name parts)
        const exact = this.config.bundleVariants.find(combination => {
          const comboParts = this.normalizeTitleParts(combination.variant_name);
          return this.sortedKey(comboParts) === exactKey;
        });
        if (exact) return exact;

        // Best subset match: every combo part in selection; prefer longest / most-specific combo
        let best = null;
        let bestScore = -1;
        this.config.bundleVariants.forEach(combination => {
          const comboParts = this.normalizeTitleParts(combination.variant_name);
          if (!comboParts.length) return;
          if (!comboParts.every(part => selectedParts.includes(part))) return;
          const score = comboParts.length;
          if (score > bestScore) {
            bestScore = score;
            best = combination;
          }
        });
        return best;
      }

      /**
       * Never disable options based on bundle matching.
       * Only undo Camouflage hide-oos false locks, and keep liquid OOS (data-available="false").
       * Button ATC state is handled only by checkCombination on selection change.
       */
      syncOptionAvailability() {
        this._syncingOptions = true;
        if (this._optionObserver) this._optionObserver.disconnect();

        this.querySelectorAll('select[data-bundle-variant-select]').forEach(select => {
          const product = this.getProductConfig(select.dataset.productId);

          Array.from(select.options).forEach(option => {
            const variant = product
              ? (product.variants || []).find(v => String(v.id) === String(option.value))
              : null;

            // Availability from linked product config / liquid only — never from bundle SKU match
            let isInStock = true;
            if (variant && typeof variant.available === 'boolean') {
              isInStock = variant.available;
            } else if (option.dataset.available === 'false') {
              isInStock = false;
            } else if (option.dataset.available === 'true') {
              isInStock = true;
            }

            if (isInStock) {
              option.disabled = false;
              option.removeAttribute('disabled');
              option.classList.remove('hide-oos-disable');
            } else {
              // True OOS from product/liquid only
              option.disabled = true;
              option.setAttribute('disabled', 'disabled');
            }
          });
        });

        this._syncingOptions = false;
        this.reattachOptionObserver();
      }

      reattachOptionObserver() {
        if (!this._optionObserver) return;
        this.querySelectorAll('select[data-bundle-variant-select]').forEach(select => {
          this._optionObserver.observe(select, {
            attributes: true,
            attributeFilter: ['disabled', 'class'],
            subtree: true,
            childList: true
          });
        });
      }

      observeOptionMutations() {
        if (typeof MutationObserver === 'undefined') return;

        this._optionObserver = new MutationObserver(() => {
          if (this._syncingOptions) return;
          if (this._syncRaf) cancelAnimationFrame(this._syncRaf);
          this._syncRaf = requestAnimationFrame(() => {
            this.syncOptionAvailability();
            this.checkCombination();
          });
        });

        this.reattachOptionObserver();
      }

      updateDiscount(totalIndividualPrice, bundlePrice) {
        if (!totalIndividualPrice || !bundlePrice) return;

        const discountAmount = totalIndividualPrice - bundlePrice;
        let discountPercent = 0;

        if (totalIndividualPrice > 0 && discountAmount > 0) {
          discountPercent = Math.round((discountAmount / totalIndividualPrice) * 100);
        }

        this.updateSaveBadge(discountPercent);
        this.updatePricingUI(totalIndividualPrice, bundlePrice, discountAmount);
      }

      updateSaveBadge(discountPercent) {
        const saveBadge = this.querySelector('[data-bundle-save-badge]');
        const savePercents = this.querySelectorAll('[data-bundle-save-percent]');

        if (saveBadge) {
          if (discountPercent > 0) {
            savePercents.forEach(el => (el.textContent = discountPercent));
            saveBadge.style.display = '';
            saveBadge.removeAttribute('hidden');
          } else {
            saveBadge.style.display = 'none';
          }
        }
      }

      updatePricingUI(totalIndividualPrice, bundlePrice, discountAmount) {
        const totalPrice = this.querySelector('[data-bundle-total-price]');
        const comparePrice = this.querySelector('[data-bundle-compare-price]');

        if (totalPrice) {
          totalPrice.innerHTML = this.formatMoney(bundlePrice);
        }

        if (comparePrice) {
          if (discountAmount > 0) {
            comparePrice.innerHTML = this.formatMoney(totalIndividualPrice);
            comparePrice.style.display = '';
            comparePrice.removeAttribute('hidden');
          } else {
            comparePrice.style.display = 'none';
          }
        }

        this.querySelectorAll('[data-bundle-dynamic-price]').forEach(el => {
          const type = el.dataset.bundleDynamicPrice;
          if (type === 'save') {
            el.innerHTML = this.formatMoney(discountAmount);
          } else {
            el.innerHTML = this.formatMoney(bundlePrice);
          }
        });
      }

      formatMoney(cents) {
        if (!cents) return '';
        if (
          window.theme &&
          window.theme.Currency &&
          typeof window.theme.Currency.formatMoney === 'function'
        ) {
          return window.theme.Currency.formatMoney(cents, theme.moneyFormat);
        }
        return '$' + (cents / 100).toFixed(2);
      }

      setAvailable(match) {
        if (this.submitButton) {
          this.submitButton.disabled = false;
        }

        if (this.buttonText) {
          let text = this.defaultButtonText;
          if (
            text.includes('{{ price }}') ||
            text.includes('{{price}}') ||
            text.includes('[price]') ||
            text.includes('[ price ]') ||
            text.includes('(price)') ||
            text.includes('( price )')
          ) {
            const type =
              text.toLowerCase().includes('save') || text.toLowerCase().includes('discount')
                ? 'save'
                : 'total';
            text = text.replace(
              /\{\{\s*price\s*\}\}|\[\s*price\s*\]|\(\s*price\s*\)/gi,
              `<span data-bundle-dynamic-price="${type}"></span>`
            );
          }
          this.buttonText.innerHTML = text;
        }

        if (this.bundleVariantIdInput) {
          this.bundleVariantIdInput.value = match.variant_id;
        }
      }

      setUnavailable() {
        if (this.submitButton) {
          this.submitButton.disabled = true;
        }

        if (this.buttonText) {
          let text = this.unavailableText;
          if (
            text.includes('{{ price }}') ||
            text.includes('{{price}}') ||
            text.includes('[price]') ||
            text.includes('[ price ]') ||
            text.includes('(price)') ||
            text.includes('( price )')
          ) {
            const type =
              text.toLowerCase().includes('save') || text.toLowerCase().includes('discount')
                ? 'save'
                : 'total';
            text = text.replace(
              /\{\{\s*price\s*\}\}|\[\s*price\s*\]|\(\s*price\s*\)/gi,
              `<span data-bundle-dynamic-price="${type}"></span>`
            );
          }
          this.buttonText.innerHTML = text;
        }

        if (this.bundleVariantIdInput) {
          this.bundleVariantIdInput.value = '';
        }
      }
    }
  );
}
